import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toPercent(value: number, total: number, decimals = 0): number {
  const v = Number(value) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  const p = (v / t) * 100;
  const k = Math.pow(10, decimals);
  return Math.round(p * k) / k;
}

export function toPercents(values: number[], decimals = 0): number[] {
  const total = values.reduce((s, v) => s + (Number(v) || 0), 0);
  return values.map((v) => toPercent(v, total, decimals));
}

// Formats numeric values for UI to avoid overly long decimals.
// Heuristics:
// - >= 1000: 0 decimals
// - 100 .. <1000: 1 decimal
// - 1 .. <100: 2 decimals
// - 0.01 .. <1: 3-4 decimals
// - < 0.01: up to 6 decimals
export function formatDisplayNumber(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '';
  const num = Number(value);
  if (!isFinite(num)) return String(value);

  const abs = Math.abs(num);
  let decimals = 2;

  if (abs >= 1000) decimals = 0;
  else if (abs >= 100) decimals = 1;
  else if (abs >= 1) decimals = 2;
  else if (abs >= 0.01) decimals = 4;
  else decimals = 6;

  // Ensure we don't show trailing zeros excessively
  const fixed = num.toFixed(decimals);
  // Trim trailing zeros and possible trailing dot
  return fixed.replace(/\.0+$/, '').replace(/(\.\d*?[1-9])0+$/, '$1');
}

export function formatUSD(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '';
  const num = Number(value);
  if (!isFinite(num)) return String(value);

  const abs = Math.abs(num);
  let maxFrac = 2;
  if (abs >= 1000) maxFrac = 0;
  else if (abs >= 100) maxFrac = 1;
  else if (abs >= 1) maxFrac = 2;
  else if (abs >= 0.01) maxFrac = 4;
  else maxFrac = 6;

  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac,
  });
  return `$${formatted}`;
}


export const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    });
  } catch {
    return 'error';
  }
};